// components/organization/CategoryScopeAccordion.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { httpRequest } from "../../utils/httpRequest";

/** ساخت ایندکس بچه‌ها بر اساس parent */
function buildChildrenIndex(list = []) {
  const byParent = new Map();
  for (const c of list) {
    const p = String(c.parent || "root");
    if (!byParent.has(p)) byParent.set(p, []);
    byParent.get(p).push(c);
  }
  return byParent;
}

/** تمام نوادگان یک نود (بازگشتی) */
function collectDescendants(id, byParent, out = []) {
  const kids = byParent.get(String(id)) || [];
  for (const ch of kids) {
    out.push(String(ch._id));
    collectDescendants(ch._id, byParent, out);
  }
  return out;
}

/** محاسبه‌ی تعداد انتخاب‌ها در زیرشاخه برای indeterminate */
function countSelectedWithDesc(id, pickedSet, byParent) {
  const all = [String(id), ...collectDescendants(id, byParent)];
  const sel = all.filter((x) => pickedSet.has(x)).length;
  return { selected: sel, total: all.length };
}

export default function CategoryScopeAccordion({
  selected = [], // string[]  → idهای انتخاب‌شده
  onChange, // (ids:string[]) => void
  title = "دسترسی به دسته‌بندی‌ها",
  hint = "ابتدا دسته‌های سطح ۱ را ببینید؛ با باز کردن هر مورد، زیرشاخه‌های همان مورد نمایش داده می‌شوند.",
}) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  // انتخاب‌شده‌ها
  const [picked, setPicked] = useState(new Set(selected.map(String)));
  useEffect(() => setPicked(new Set((selected || []).map(String))), [selected]);

  // آکاردئون‌ها
  const [openL1, setOpenL1] = useState(new Set()); // idهای لول۱ باز
  const [openL2, setOpenL2] = useState(new Set()); // idهای لول۲ باز

  // دریافت همه‌ی دسته‌ها
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const token = JSON.parse(localStorage.getItem("user") || "{}")?.token;
        const res = await httpRequest(
          "GET",
          "/category",
          {},
          { "x-access-token": token }
        );
        const arr = res?.data?.data || res?.data || [];
        setCats(Array.isArray(arr) ? arr : []);
      } catch {
        setCats([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // نماهای کمکی
  const level1 = useMemo(
    () => cats.filter((c) => Number(c.level) === 1),
    [cats]
  );
  const byParent = useMemo(() => buildChildrenIndex(cats), [cats]);

  const applyPicked = (next) => {
    setPicked(next);
    onChange?.([...next]);
  };

  /** تیک/لغو آبشاری برای یک id (خودش + همه‌ی نوادگان) */
  const toggleCascade = (id, checked) => {
    const all = [String(id), ...collectDescendants(id, byParent)];
    applyPicked(
      ((prev) => {
        const next = new Set(prev);
        if (checked) all.forEach((x) => next.add(x));
        else all.forEach((x) => next.delete(x));
        return next;
      })(picked)
    );
  };

  /** اینپوت چک‌باکس با پشتیبانی از indeterminate */
  const CascadeCheckbox = ({ nodeId }) => {
    const { selected: sel, total } = countSelectedWithDesc(
      nodeId,
      picked,
      byParent
    );
    const checked = sel === total && total > 0;
    const indeterminate = sel > 0 && sel < total;
    const ref = useRef(null);
    useEffect(() => {
      if (ref.current) ref.current.indeterminate = indeterminate;
    }, [indeterminate]);
    return (
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        onChange={(e) => toggleCascade(nodeId, e.target.checked)}
        className="h-4 w-4 accent-purple-600"
      />
    );
  };

  /** باز/بسته‌کردن یک لول */
  const toggleOpen = (setFn, setVal, id) => {
    setFn((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    // اگر لول۱ بسته شد، لول۲ زیرشاخه‌هایش را هم ببند
    if (setVal === "l1") {
      setOpenL2((prev) => {
        const next = new Set(prev);
        // همه‌ی لول۲های زیر این لول۱ را ببند
        const l2s = byParent.get(id) || [];
        for (const l2 of l2s) next.delete(String(l2._id));
        return next;
      });
    }
  };

  // رندر یک آیتم لول۲ به همراه دراپ‌داون لول۳
  const renderLevel2 = (parentId) => {
    const children = byParent.get(String(parentId)) || [];
    return (
      <div className="space-y-2 pl-6 mt-2">
        {children
          .filter((c) => Number(c.level) === 2)
          .map((l2) => {
            const l2Id = String(l2._id);
            const isOpen = openL2.has(l2Id);
            const l3 = (byParent.get(l2Id) || []).filter(
              (c) => Number(c.level) === 3
            );
            const hasL3 = l3.length > 0;

            return (
              <div
                key={l2Id}
                className="rounded-lg border border-gray-200 bg-white"
              >
                {/* summary لول۲ */}
                <div
                  className="px-3 py-2 flex items-center gap-2 cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleOpen(setOpenL2, "l2", l2Id)}
                >
                  <span
                    className={`text-xs transition-transform ${
                      isOpen ? "rotate-90" : ""
                    }`}
                  >
                    ▸
                  </span>
                  <CascadeCheckbox nodeId={l2Id} />
                  <span className="font-medium truncate">{l2.title}</span>
                  <span className="text-[10px] text-gray-500 ms-1">L2</span>
                </div>

                {/* بدنه لول۳ */}
                {isOpen && hasL3 && (
                  <div className="pl-6 pr-3 pb-3 pt-1 space-y-2">
                    {l3.map((leaf) => {
                      const leafId = String(leaf._id);
                      return (
                        <label
                          key={leafId}
                          className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-gray-50"
                        >
                          <CascadeCheckbox nodeId={leafId} />
                          <span className="truncate">{leaf.title}</span>
                          <span className="text-[10px] text-gray-500 ms-1">
                            L3
                          </span>
                        </label>
                      );
                    })}
                    {!l3.length && (
                      <div className="text-[12px] text-gray-500 px-2">
                        زیر‌دسته‌ای ندارد
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    );
  };

  return (
    <div className="mt-4">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <h3 className="text-sm font-bold mb-1">{title}</h3>
          <p className="text-xs text-gray-500">{hint}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setOpenL1(new Set());
            setOpenL2(new Set());
          }}
          className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
        >
          بستن همه
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-gray-500">
          در حال بارگذاری دسته‌بندی‌ها…
        </div>
      ) : !level1.length ? (
        <div className="text-xs text-gray-500">دسته‌بندی سطح ۱ یافت نشد</div>
      ) : (
        <div className="space-y-2">
          {level1.map((l1) => {
            const l1Id = String(l1._id);
            const isOpen = openL1.has(l1Id);
            return (
              <div
                key={l1Id}
                className="rounded-xl border border-gray-300 bg-white"
              >
                {/* summary لول۱ */}
                <div
                  className="px-3 py-2 flex items-center gap-2 cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleOpen(setOpenL1, "l1", l1Id)}
                >
                  <span
                    className={`text-xs transition-transform ${
                      isOpen ? "rotate-90" : ""
                    }`}
                  >
                    ▸
                  </span>
                  <CascadeCheckbox nodeId={l1Id} />
                  <span className="font-medium truncate">{l1.title}</span>
                  <span className="text-[10px] text-gray-500 ms-1">L1</span>
                </div>

                {/* بدنه: لول۲ (و داخلش لول۳) */}
                {isOpen && renderLevel2(l1Id)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
